import { useState, useEffect } from 'react';

import {          
  DndContext,
  closestCenter,    
  PointerSensor,
  useSensor,
  useSensors,       
} from "@dnd-kit/core";
import {
  arrayMove,      
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import TopNavBar from '../components/TopNavBar';
import SideNavBar from '../components/SideNavBar';

import Refreshing from '../components/Refreshing';

// Our libraries
import * as logger from '../logger';
import * as apiComms from '../apiComms';

// Components
import PromotionCardComponent from '../components/PromotionCardComponent';

import * as dayjs from 'dayjs'

import { IoMdSettings } from "react-icons/io";
import { FaTrashAlt } from "react-icons/fa";
import { MdOutlineAddPhotoAlternate } from "react-icons/md";
import { IoMdAddCircleOutline } from "react-icons/io";

import PromotionAddOrEditModal from '../modals/PromotionAddOrEditModal';
import DeletePromotionItemModal from '../modals/DeletePromotionItemModal';
import useIsMobile, { DESKTOP_CONTENT_MARGIN_LEFT, MOBILE_CONTENT_MARGIN_LEFT } from '../hooks/useIsMobile';

const PROG = 'OffersAndPromotionsHomePagePromotions.js';

const OffersAndPromotionsHomePagePromotions = (props) => {

  const [showInfoBox, setShowInfoBox] = useState(true);
  const [displayPromotions, setDisplayPromotions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [isAddPromotionModalVisible, setIsAddPromotionModalVisible] = useState(false);
  const [isAddOrEditPromotionModalRefreshing, setIsAddOrEditPromotionModalRefreshing] = useState(false);
  const [editPromotionItem, setEditPromotionItem] = useState(null);
  const [isEditPromotionModalVisible, setIsEditPromotionModalVisible] = useState(false);
  const [deletePromotionItem, setDeletePromotionItem] = useState(null);
  const [isDeletePromotionModalVisible, setIsDeletePromotionModalVisible] = useState(false);

  const [isInfoToastVisible, setIsInfoToastVisible ] = useState(false);
  const [infoToastMessage, setInfoToastMessage ] = useState('');
  const isMobile = useIsMobile();

  

  useEffect(() => {
    refreshPromotions();
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5, // small drag threshold
      },
    })
  );
    
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }

    if (active.id !== over.id) {
      const oldIndex = displayPromotions.findIndex((item) => item.promotionId === active.id);
      const newIndex = displayPromotions.findIndex((item) => item.promotionId === over.id);
      const updatedRecords = arrayMove(displayPromotions, oldIndex, newIndex);
      setDisplayPromotions(arrayMove(displayPromotions, oldIndex, newIndex)); // Instant Update
      updateItemOrderInCloud(updatedRecords);
    }
  };

  const updateItemOrderInCloud = async (updatedRecords) => {
    const FUNCTION = 'updateItemOrderInCloud';
  
    // Show Info Toast
    setInfoToastMessage('Saving...');
    setIsInfoToastVisible(true);

    let updates = [];
    for (let i=0;i<updatedRecords.length;i++) {
      const promotion = updatedRecords[i];
      const updateRec = {
        promotionId : promotion.promotionId,
        orderId : i
      };
      updates.push(updateRec);
    }

    const res = await apiComms.updatePromotionsOrders(updates);
    logger.debug('updatePromotionOrders response', { PROG, FUNCTION, res });

    if (res.isErr) {
      // An error occured
      setInfoToastMessage('Error Occured: '+res.errMsg);
      setTimeout(() => {
        setIsInfoToastVisible(false);
      }, 2000);
      // Revert the order changes
      setDisplayPromotions((prevDisplayPromotions) => 
        [...prevDisplayPromotions].sort((a, b) => {
          const a_orderId = a.orderId;
          const b_orderId = b.orderId;
          console.log('Comparing '+a_orderId+' against '+b_orderId);
          if (a_orderId < b_orderId) {
            return -1;
          }
          else if (a_orderId > b_orderId) {
            return 1;
          }
          else {
            return 0;
          }
        }
      ));
    }
    else {
      // Success
      setInfoToastMessage('Success: Updated !');
      setTimeout(() => {
        setIsInfoToastVisible(false);
      }, 2000);
      let updateOrderIds = {};
      for (let i=0;i<updatedRecords.length;i++) {
        updateOrderIds[updatedRecords[i].promotionId] = i;
      }
      // Update displayPromotions Array
      displayPromotions.map((promotion) => {
        promotion.orderId = updateOrderIds[promotion.promotionId];
      });
      // Resort the displayPromotions Array
      setDisplayPromotions((prevDisplayPromotions) => 
        [...prevDisplayPromotions].sort((a, b) => {
          const a_orderId = a.orderId;
          const b_orderId = b.orderId;
          console.log('Comparing '+a_orderId+' against '+b_orderId);
          if (a_orderId < b_orderId) {
            return -1;
          }
          else if (a_orderId > b_orderId) {
            return 1;
          }
          else {
            return 0;
          }
        }
      ));
    }
  };

  const refreshPromotions = async () => {
    const FUNCTION = 'refreshPromotions';
      
    setIsLoading(true);
  
    const promotionsRes = await apiComms.getAllPromotions();
    if (!promotionsRes || promotionsRes.isErr) {
      if (promotionsRes) {
        alert('Sorry an error occured: '+promotionsRes.errMsg);
      }
      else {
        alert('Sorry an Network error occured');
      }
    }
    else {
      let promotions = promotionsRes.response.promotions;
      _enrichPromotions(promotions);

      // Sort promotions 09/Jan/2026
      promotions.sort((a, b) => {
        const a_orderId = a.orderId;
        const b_orderId = b.orderId;
        if (a_orderId < b_orderId) {
          return -1;
        }
        else if (a_orderId > b_orderId) {
          return 1;
        }
        else {
          return 0;
        }
      });

      setDisplayPromotions(promotions);
    }
    setIsLoading(false);
  };

  const _enrichPromotions = (promotions) => {
    const FUNCTION = '_enrichPromotions';
    for (let i=0;i<promotions.length;i++) {
      let promotion = promotions[i];
      const displayFrom = dayjs((promotion.displayFrom * 1000));
      const displayTo = dayjs((promotion.displayTo * 1000));
      const now = dayjs();
      const isLive = (displayFrom.isBefore(now) && displayTo.isAfter(now));
      if (isLive) {
        promotion.enrichedData = { displayStatus : 'LIVE' };
      }
      else {
        promotion.enrichedData = { displayStatus : 'NOT LIVE' };
      }
    }
  };

  const handleCancelEditPromotionModalPress = () => {
    setIsEditPromotionModalVisible(false);
  };

  const handleCancelNewPromotionModalPress = () => {
    setIsAddPromotionModalVisible(false);
  };

  const handleUpdatePromotionsEvent = () => {
    setIsAddPromotionModalVisible(false);
    refreshPromotions();
  };

  const handleEditPromotionItem = (promotion) => {
    console.log('Edit Promotion: '+JSON.stringify(promotion));
    setEditPromotionItem(promotion);
    setIsEditPromotionModalVisible(true);
  };

  const handleDeletePromotionItem = (promotion) => {
    setDeletePromotionItem(promotion);
    setIsDeletePromotionModalVisible(true);
  };

  const handleConfirmDeletePromotionItem = async (promotion) => {

    setIsDeletePromotionModalVisible(false);
    setIsLoading(true);

    const promotionId = promotion.promotionId;

    const res = await apiComms.deletePromotion(promotionId);
    

    setIsLoading(false);

    refreshPromotions();

  };

  const handleEditPromotion = async (promotion) => {
    const FUNCTION = 'handleEditPromotion';


    // Make the AddOrEdit Modal Show it's refreshing view
    setIsAddOrEditPromotionModalRefreshing(true);


    const res = await apiComms.updateExistingPromotion(
      promotion.promotionId,
      promotion.title,
      promotion.linkUrl,
      promotion.imageUrl,
      promotion.displayFrom,
      promotion.displayTo
    );            
    console.log('Response from updatePromotion call: '+JSON.stringify(res));

    setIsAddOrEditPromotionModalRefreshing(false);
    setIsEditPromotionModalVisible(false);

    // Reload all our Promotion Items
    refreshPromotions();
  };
  
    
    

  const handleAddNewPromotion = async (promotion) => {
    const FUNCTION = 'handleAddNewPromotion';

    // Hide the PromotionAddOrEditModal Modal
    //setIsAddPromotionModalVisible(false);

    // Make the AddOrEdit Modal Show it's refreshing view
    setIsAddOrEditPromotionModalRefreshing(true);

    const res = await apiComms.addNewPromotion(
      promotion.title,
      promotion.linkUrl,
      promotion.imageUrl,
      promotion.displayFrom,
      promotion.displayTo
    );            
    console.log('Response from addNewPromotion call: '+JSON.stringify(res));

    setIsAddPromotionModalVisible(false);
    setIsAddOrEditPromotionModalRefreshing(false);
  
    // Reload all our Promotion Items
    refreshPromotions();
  };

  return (
    <>
      { isAddPromotionModalVisible ? (
        <PromotionAddOrEditModal handleCancelPress = { handleCancelNewPromotionModalPress } handleSubmitButtonPress = { handleAddNewPromotion } modalTitle='ADD NEW PROMOTION' modalKeyword='Promotion' isLinkUrl = { true } submitButtonText='Add New' isRefreshing = { isAddOrEditPromotionModalRefreshing } />
      ) : null }
      { isEditPromotionModalVisible ? (
        <PromotionAddOrEditModal handleCancelPress = { handleCancelEditPromotionModalPress } handleSubmitButtonPress = { handleEditPromotion } modalTitle='EDIT PROMOTION' modalKeyword='Promotion' isLinkUrl = { true } submitButtonText='Update' isRefreshing = { isAddOrEditPromotionModalRefreshing } existingPromotion = { editPromotionItem } />
      ) : null }
      { isDeletePromotionModalVisible ? (
        <DeletePromotionItemModal promotion = { deletePromotionItem } modalTitle='DELETE PROMOTION' modalKeyword='Promotion' isLinkUrl = { true } handleCancelPress = { () => { setIsDeletePromotionModalVisible(false); }} handleDeleteButtonPress = { (promotion) => { handleConfirmDeletePromotionItem(promotion); }} />
      ) : null }





      <TopNavBar handleUpdate = { props.handleUpdate } activeRoute = { props.activeRoute } />
      <SideNavBar handleUpdate = { props.handleUpdate } activeRoute = { props.activeRoute } />
      <div style={{ marginLeft: isMobile ? MOBILE_CONTENT_MARGIN_LEFT : DESKTOP_CONTENT_MARGIN_LEFT, marginRight : 10, marginTop : 0, paddingTop : 45 }}>
        <h1 style={{ fontSize: 18, fontWeight : 'bold', paddingTop : 20, marginLeft : 10 }}>Offers & Promotions > Home Page Promotions</h1>
        { showInfoBox ? (
          <div style={{ marginTop : 20 }} className="alert alert-primary lert-dismissible fade show" role="alert">
            <h4 className="alert-heading">About Offers & Promotions</h4>
            <p>These Offer & Promotion items are displayed in a horizontal carousel on both the Home view and the V.I.P. Offers view in the Petstop App.  When pressed then display a pop-up view of the Offer & Promotion website link typically featuring one of your products, posts or articles.</p>
            <button style={{ position : 'absolute', top : 10, right : 10 }} type="button" className="btn-close" data-bs-dismiss="alert" aria-label="Close" onClick={ () => { setShowInfoBox(false); }} onPress={ () => { setShowInfoBox(false); }}></button>
          </div>
        ) : null }
        

        { isLoading ? (
          <Refreshing message='Loading Promotions...' />
        ) : null }

        { !isLoading ? (
          <>
            <div style={{ display : 'flex', flexWrap : 'wrap', height : '100%', width : '100%', flexDirection : 'row', justifyContent : 'flex-start', alignItems : 'center' }}>
              <div style={{ width : '100%' }}>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext items={displayPromotions.map((item) => item.promotionId)} strategy={rectSortingStrategy}> 
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fill, minmax(256px, 1fr))",
                      gap: 5,
                      padding: 0,
                    }}
                  >
                    { displayPromotions.map((promotion, i) => {
                      const promotionId = promotion.promotionId;
                      return (
                        <PromotionCardComponent
                          key = { promotionId }
                          id = { promotionId }
                          promotion = { promotion }
                          handleEditPromotionItem = { handleEditPromotionItem }
                          handleDeletePromotionItem = { handleDeletePromotionItem }
                          totalItems = { displayPromotions.length }
                        />
                      );
                    })}
                  </div>
                </SortableContext>
              </DndContext>
              </div>

            </div>
            <button
              type="button"
              className="btn btn-info position-fixed bottom-0 end-0 m-4 rounded-circle"
              style={{ width : 56, height : 56 }}
              onClick = { () => { setIsAddPromotionModalVisible(true); }}
              onPress = { () => { setIsAddPromotionModalVisible(true); }}
            >
              +
            </button>
          </>
        ) : null }

        
      </div>

      { isInfoToastVisible ? (
        <div className="toast-container position-fixed bottom-0 end-0 p-3">
          <div className="toast show text-bg-info text-white">
            <div className="d-flex">
              <div className="toast-body">
                { infoToastMessage }
              </div>
              <button type="button" className="btn-close me-2 m-auto bg-white" data-bs-dismiss="toast" aria-label="Close" onClick = { () => { setIsInfoToastVisible(false); }} onPress = { () => { setIsInfoToastVisible(false); }}></button>
            </div>
          </div>
        </div>
      ) : null }

    </>
  );

};

export default OffersAndPromotionsHomePagePromotions;
